import { getGender, getAnswerVariant } from '../'
import {
	QuestionAnswerType,
	isChoiceGroupAnswerType,
	isSliderAnswerType,
	BasicAnswerType,
	RecommendationPropertyType,
	ChoiceGroupAnswerType,
} from '../../types'

type PropertiesType = {
	recommendation: RecommendationPropertyType[]
	screening: ScreeningDTO[]
}

type ScreeningDTO = {
	[key: string]: unknown
	id: string
	title: string
	value?: string
}

const getSelectedIdsFromChoiceGroupAnswerType = (
	answers: ChoiceGroupAnswerType[]
): string[] => {
	return answers.reduce((result: string[], answer: ChoiceGroupAnswerType) => {
		if (answer.selected) {
			result.push(answer.id)
		}

		if (answer?.answers) {
			answer.answers.forEach((subAnswer: ChoiceGroupAnswerType) => {
				if (subAnswer.selected) result.push(subAnswer.id)
			})
		}
		return result
	}, [])
}

const generatePageAnswer = (confirmedAnswer: QuestionAnswerType) => {
	switch (true) {
		case Array.isArray(confirmedAnswer.answer) &&
			isChoiceGroupAnswerType(confirmedAnswer.answer[0]): {
			const selectedAnswers = Array.isArray(confirmedAnswer?.answer)
				? getSelectedIdsFromChoiceGroupAnswerType(confirmedAnswer.answer)
				: []

			return {
				$type: getAnswerVariant(confirmedAnswer.type),
				SurveyPageId: confirmedAnswer.questionId,
				Selected: selectedAnswers.join(','), // Join the selected answers into a string
				Tags: Array.isArray(confirmedAnswer?.tags)
					? confirmedAnswer.tags.filter(Boolean).join(',')
					: '',
				Properties: extractProperties(confirmedAnswer.properties),
			}
		}

		case confirmedAnswer.type === 'slider': {
			if (!isSliderAnswerType(confirmedAnswer.answer)) return null

			return {
				$type: getAnswerVariant(confirmedAnswer.type),
				SurveyPageId: confirmedAnswer.questionId,
				SliderLevel: confirmedAnswer.answer.sliderIndex.toString(),
				Tags: confirmedAnswer.tags,
				Properties: extractProperties(confirmedAnswer.properties),
			}
		}

		case confirmedAnswer.type === 'basic': {
			const newAnswers = confirmedAnswer.answer as BasicAnswerType
			const answer: {
				$type: string
				SurveyPageId: string
				Properties: string
				Tags: string | Array<string>
				Gender?: number
				Birthday?: string
				Height?: number
				Weight?: number
			} = {
				$type: getAnswerVariant(confirmedAnswer.type),
				SurveyPageId: confirmedAnswer.questionId,
				Properties: '',
				Tags: confirmedAnswer.tags,
			}

			const propertiesMap = {
				gender: () => {
					if (newAnswers.gender && getGender(newAnswers.gender) >= 0) {
						answer.Gender = getGender(newAnswers.gender)
						answer.Properties = answer.Properties.concat(
							`Gender:${getGender(newAnswers.gender)};`
						)
					}
				},
				birthday: () => {
					if (newAnswers.birthday) {
						answer.Birthday = newAnswers.birthday
						answer.Properties = answer.Properties.concat(
							`Birthday:${newAnswers.birthday};`
						)
					}
				},
				height: () => {
					if (newAnswers.height && newAnswers.height > 0) {
						answer.Height = newAnswers.height
						answer.Properties = answer.Properties.concat(
							`Height:${newAnswers.height};`
						)
					}
				},
				weight: () => {
					if (newAnswers.weight && newAnswers.weight > 0) {
						answer.Weight = newAnswers.weight
						answer.Properties = answer.Properties.concat(
							`Weight:${newAnswers.weight};`
						)
					}
				},
			}

			for (const key of Object.keys(propertiesMap)) {
				propertiesMap[key as keyof typeof propertiesMap]()
			}

			if (answer.Properties === '') return null
			return answer
		}
		default: {
			throw new Error('Invalid answer type')
		}
	}
}

const extractProperties = (properties?: PropertiesType): string => {
	if (!properties) return ''

	const { recommendation, screening } = properties

	return (
		JSON.stringify({
			recommendation: recommendation,
			screening: screening.map(
				(item): ScreeningDTO => ({
					id: item.id,
					title: item.title,
					value: item.value,
				})
			),
		}) ?? ''
	)
}

export default generatePageAnswer
